import React, { useEffect, useState, useRef } from "react";

export default function MockTest({ initialQuestions, autoStart = false }) {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(null);
  const [restored, setRestored] = useState(false);
  const [started, setStarted] = useState(autoStart);
  const firstUnansweredRef = useRef(null);

  const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  useEffect(() => {
    const loadQuestions = (data) => {
      const valid = data.filter((q) => !q.skip);
      setQuestions(shuffle(valid));
    };

    if (initialQuestions && initialQuestions.length) {
      loadQuestions(initialQuestions);
      return;
    }

    // Reuse global cache/promise to avoid multiple network requests
    if (window.__mockQuestionsData) {
      loadQuestions(window.__mockQuestionsData);
      return;
    }

    if (window.__mockQuestionsPromise) {
      window.__mockQuestionsPromise
        .then((questionsArray) => loadQuestions(questionsArray))
        .catch(() => {});
      return;
    }

    window.__mockQuestionsPromise = fetch(process.env.PUBLIC_URL+ "/mock_questions.json")
      .then((r) => r.json())
      .then((data) => {
        console.log("El process: " + process.env.PUBLIC_URL)
        const questionsArray = data.questions || [];
        window.__mockQuestionsData = questionsArray;
        delete window.__mockQuestionsPromise;
        return questionsArray;
      });

    window.__mockQuestionsPromise
      .then((questionsArray) => loadQuestions(questionsArray))
      .catch(() => {});
  }, [initialQuestions]);

  useEffect(() => {
    const saved = localStorage.getItem("mockTestProgress");
    if (saved) {
      const parsed = JSON.parse(saved);
      const hasProgress =
        (parsed.answers && Object.keys(parsed.answers).length > 0) ||
        parsed.score !== null;

      setAnswers(parsed.answers || {});
      setSubmitted(parsed.submitted || false);
      setScore(parsed.score || null);
      setRestored(hasProgress);
      setStarted(hasProgress);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "mockTestProgress",
      JSON.stringify({ answers, submitted, score })
    );
  }, [answers, submitted, score]);

  const categories = questions.reduce((acc, q) => {
    acc[q.category] = (acc[q.category] || 0) + 1;
    return acc;
  }, {});

  const handleSelect = (qIndex, value, allowsMultiple) => {
    if (allowsMultiple) {
      const prev = answers[qIndex] || [];
      const updated = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];
      setAnswers({ ...answers, [qIndex]: updated });
    } else {
      setAnswers({ ...answers, [qIndex]: value });
    }
  };

  const handleSubmit = () => {
    const unanswered = questions
      .map((q, i) => {
        if (q.allows_multiple) {
          return answers[i] && answers[i].length > 0 ? null : i;
        } else {
          return answers[i] ? null : i;
        }
      })
      .filter((x) => x !== null);

    if (unanswered.length > 0) {
      setSubmitted(false);
      firstUnansweredRef.current = unanswered[0];
      document
        .getElementById(`question-${firstUnansweredRef.current}`)
        ?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    if (window.confirm("Está seguro que desea finalizar?")) {
      let correctCount = 0;
      questions.forEach((q, i) => {
        if (q.allows_multiple) {
          const selected = answers[i] || [];
          const correctValues = q.options
            .filter((o) => o.correct)
            .map((o) => o.value)
            .sort();
          const selectedSorted = [...selected].sort();
          if (
            JSON.stringify(correctValues) === JSON.stringify(selectedSorted)
          ) {
            correctCount++;
          }
        } else {
          const selected = answers[i];
          const option = q.options.find((o) => o.value === selected);
          if (option?.correct) correctCount++;
        }
      });
      setScore(correctCount);
      setSubmitted(true);
    }
  };

  const handleReset = () => {
    if (window.confirm("Resetear e iniciar una nueva evaluacion?")) {
      localStorage.removeItem("mockTestProgress");
      setAnswers({});
      setSubmitted(false);
      setScore(null);
      setRestored(false);
      setStarted(false);
      setQuestions((prev) => shuffle(prev));
    }
  };

  const handleStart = () => setStarted(true);

  return (
    <div className="container-fluid px-3 px-sm-4 pb-5 quiz-container">
      <style>{`
        @media (min-width: 992px) {
          .quiz-container {
            max-width: 900px;
            margin: 0 auto;
          }
          .quiz-card {
            margin-left: auto;
            margin-right: auto;
          }
          .form-check {
            padding-left: 2rem;
          }
        }
        img.question-image {
          display: block;
          max-width: 100%;
          height: auto;
          margin: 1rem auto;
          border-radius: 0.5rem;
          box-shadow: 0 0 5px rgba(0,0,0,0.1);
        }
      `}</style>

      {!started ? (
        <div className="mt-3">
          <h5>Total Preguntas: {questions.length}</h5>
          <h6>Categorias:</h6>
          <ul>
            {Object.entries(categories).map(([cat, count]) => (
              <li key={cat}>
                {cat}: {count}
              </li>
            ))}
          </ul>
          <button className="btn btn-success mt-2" onClick={handleStart}>
            ▶️ Comenzar Prueba
          </button>
        </div>
      ) : (
        <>
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3">
            <div className="mb-2 mb-sm-0">
              <h5 className="mb-0">
                Total Preguntas: {questions.length}
                <br />
                Categorias:
              </h5>
              <ul className="mb-0">
                {Object.entries(categories).map(([cat, count]) => (
                  <li key={cat}>
                    {cat}: {count}
                  </li>
                ))}
              </ul>
            </div>
            <button className="btn btn-outline-danger" onClick={handleReset}>
              🔄 Reiniciar Prueba
            </button>
          </div>

          {restored && (
            <div className="alert alert-warning py-2">
              Progreso restaurado
            </div>
          )}

          {questions.map((q, i) => (
            <div
              key={i}
              id={`question-${i}`}
              className="card my-3 p-3 shadow-sm quiz-card"
              style={{
                border:
                  score === null &&
                  (!answers[i] ||
                    (Array.isArray(answers[i]) && answers[i].length === 0))
                    ? "2px solid red"
                    : "1px solid #ccc",
              }}
            >
              <h6>
                <b>
                  {i + 1}. ({q.category}) {q.question}
                </b>
              </h6>

              {/* ✅ Show image if present */}
              {q.image && (
                <img
                  src={require(`../images/${q.image}`)}
                  alt={q.question}
                  className="question-image"
                />
              )}

              {q.allows_multiple && (
                <p className="text-muted small mb-1">
                  ⚠️ Se admiten múltiples respuestas
                </p>
              )}

              {q.options.map((opt, j) => {
                const optionId = `q${i}-opt${j}`;
                const isChecked = q.allows_multiple
                  ? (answers[i] || []).includes(opt.value)
                  : answers[i] === opt.value;
                return (
                  <div className="form-check my-1" key={j}>
                    <input
                      id={optionId}
                      type={q.allows_multiple ? "checkbox" : "radio"}
                      className="form-check-input"
                      name={`q-${i}`}
                      value={opt.value}
                      checked={isChecked}
                      disabled={submitted}
                      onChange={(e) =>
                        handleSelect(i, e.target.value, q.allows_multiple)
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor={optionId}
                      style={{ cursor: "pointer" }}
                    >
                      {opt.value}
                    </label>
                  </div>
                );
              })}

              {/* Results */}
              {submitted && (
                <div className="mt-2">
                  {(() => {
                    const selected = answers[i];
                    const correctValues = q.options
                      .filter((o) => o.correct)
                      .map((o) => o.value);
                    let correct = false;

                    if (q.allows_multiple) {
                      const selectedSorted = [...(selected || [])].sort();
                      const correctSorted = [...correctValues].sort();
                      correct =
                        JSON.stringify(selectedSorted) ===
                        JSON.stringify(correctSorted);
                    } else {
                      const opt = q.options.find((o) => o.value === selected);
                      correct = opt?.correct || false;
                    }

                    if (correct) {
                      return (
                        <div>
                          <span className="text-success">✅ Correcto</span>
                          {q.reason && (
                            <div
                              className="text-muted small mt-1"
                              dangerouslySetInnerHTML={{
                                __html: `Fundamento: ${q.reason}`,
                              }}
                            />
                          )}
                        </div>
                      );
                    } else {
                      const correctOpts = q.options.filter((o) => o.correct);
                      return (
                        <div>
                          <span className="text-danger">❌ Incorrecto</span>
                          {correctOpts.length > 0 && (
                            <p className="text-muted small mb-0">
                              Respuesta Correcta
                              {correctOpts.length > 1 ? "s" : ""}:{" "}
                              <b>
                                {correctOpts.map((o) => o.value).join(", ")}
                              </b>
                            </p>
                          )}
                          {q.reason && (
                            <div
                              className="text-muted small mt-1"
                              dangerouslySetInnerHTML={{
                                __html: `Reason: ${q.reason}`,
                              }}
                            />
                          )}
                        </div>
                      );
                    }
                  })()}
                </div>
              )}
            </div>
          ))}

          {score === null && (
            <button className="btn btn-primary w-100" onClick={handleSubmit}>
              Enviar
            </button>
          )}

          {score !== null && (
            <div className="alert alert-info mt-3 text-center">
              <b>Su puntaje:</b> {score} / {questions.length}
            </div>
          )}
        </>
      )}
    </div>
  );
}
